import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { marketingUrl } from "@/lib/domains";
import { toast } from "sonner";
import { ArrowLeft, Mail, MessageCircle, Smartphone } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthModeTabs } from "@/components/auth/AuthModeTabs";
import { DEFAULT_COUNTRY, PhoneInput, buildE164, formatPhoneDisplay, isValidPhone } from "@/components/auth/PhoneInput";
import { BrandArtwork, BrandLockup, PoweredByAdmissionHall } from "@/components/Brand";

const Field = ({ label, testId, ...rest }) => (
  <label className="block">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    <input
      data-testid={testId}
      {...rest}
      className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A365D]"
    />
  </label>
);

export default function Login() {
  const [mode, setMode] = useState("phone");
  const [sent, setSent] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_COUNTRY.iso);
  const [phoneDigits, setPhoneDigits] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [smsCodeLength, setSmsCodeLength] = useState(4);
  const [busy, setBusy] = useState(false);
  const { login, user } = useAuth();
  const nav = useNavigate();

  const phoneE164 = buildE164(phoneCountry, phoneDigits);

  useEffect(() => {
    if (user) {
      if (user.role !== "admin" && user.profile_complete && user.onboarding_complete === false) {
        nav("/onboarding", { replace: true });
        return;
      }
      nav(user.role === "admin" ? "/admin" : "/app", { replace: true });
    }
  }, [user, nav]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sid = hash.split("session_id=")[1].split("&")[0];
      api
        .post("/auth/google/session", { session_id: sid })
        .then(({ data }) => {
          login(data.user);
          window.location.hash = "";
          nav("/app/allotment", { replace: true });
        })
        .catch((e) => toast.error(errMsg(e)));
    }
  }, [login, nav]);

  const sendCode = async () => {
    setBusy(true);
    try {
      if (mode === "phone" || mode === "sms") {
        const path = mode === "sms" ? "/auth/sms/request" : "/auth/otp/request";
        const { data } = await api.post(path, { phone: phoneE164 });
        setDevCode(data.dev_code || null);
        if (mode === "sms") {
          setSmsCodeLength(data.code_length);
          toast.success("Code sent by SMS");
        } else {
          toast[data.delivered ? "success" : "message"](
            data.delivered ? "Code sent on WhatsApp" : "WhatsApp unavailable — code shown below",
          );
        }
      } else {
        await api.post("/auth/email/request", { email });
        setDevCode(null);
        toast.success(`Code emailed to ${email}`);
      }
      setSent(true);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const path = mode === "phone"
        ? "/auth/otp/verify"
        : mode === "sms"
          ? "/auth/sms/verify"
          : "/auth/email/verify";
      const body = mode === "email" ? { email, code } : { phone: phoneE164, code };
      const { data } = await api.post(path, body);
      login(data.user);
      if (data.user.role !== "admin" && data.user.profile_complete && data.user.onboarding_complete === false) {
        toast.success("Almost there — a few quick details");
        nav("/onboarding", { replace: true });
      } else {
        toast.success("Welcome to NeetOne");
        nav("/app", { replace: true });
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    const redirect = `${window.location.origin}/login`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const switchMode = (m) => {
    setMode(m);
    setSent(false);
    setCode("");
    setDevCode(null);
  };

  const isPhoneMode = mode === "phone" || mode === "sms";
  const requiredCodeLength = mode === "sms" ? smsCodeLength : 6;
  const canSend = isPhoneMode
    ? isValidPhone(phoneCountry, phoneDigits)
    : /\S+@\S+\.\S+/.test(email);

  return (
    <div className="grid min-h-screen lg:grid-cols-[1fr_1.1fr]">
      <div className="hidden flex-col justify-between bg-[#1A365D] p-12 text-white lg:flex">
        <a href={marketingUrl()} data-testid="login-brand" aria-label="NeetOne home" className="inline-flex w-fit">
          <BrandLockup size="lg" tone="light" wordmark="always" />
        </a>
        <div>
          <h2 className="font-display text-4xl font-bold leading-tight tracking-tight">
            Allotments, closing ranks, fees and colleges — in one filterable place.
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-blue-100">
            Karnataka KEA 2025 is live. Verify your mobile number to start exploring on the Free plan.
          </p>
        </div>
        <PoweredByAdmissionHall tone="light" />
      </div>

      <div className="flex items-center justify-center bg-[#FAFAFA] px-6 py-16">
        <div className="w-full max-w-sm">
          <a
            href={marketingUrl()}
            data-testid="back-home-link"
            className="mb-8 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-[#1A365D]"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
          </a>

          <div className="mb-6 flex items-center justify-between lg:hidden">
            <BrandLockup size="md" wordmark="always" />
            <PoweredByAdmissionHall />
          </div>

          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
            {sent ? "Enter your code" : "Sign in"}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {sent
              ? mode === "email"
                ? `We sent a 6-digit code to ${email}.`
                : `We sent a code to ${formatPhoneDisplay(phoneCountry, phoneDigits)}.`
              : "Use WhatsApp, SMS, or email to verify your NeetOne account."}
          </p>

          {!sent && (
            <>
              <AuthModeTabs mode={mode} onChange={switchMode} />

              <div className="mt-5 space-y-4">
                {isPhoneMode ? (
                  <PhoneInput
                    label={mode === "sms" ? "SMS number" : "WhatsApp number"}
                    testIdPrefix="login-phone"
                    iso={phoneCountry}
                    onCountry={setPhoneCountry}
                    digits={phoneDigits}
                    onDigits={setPhoneDigits}
                  />
                ) : (
                  <Field
                    label="Email address"
                    testId="login-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                )}
                {mode === "email" && (
                  <p data-testid="email-phone-notice" className="text-xs text-slate-500">
                    We will ask for a verified mobile number right after sign-in.
                  </p>
                )}
                <button
                  data-testid="send-otp-btn"
                  disabled={busy || !canSend}
                  onClick={sendCode}
                  className="flex h-11 w-full items-center justify-center gap-2 bg-[#1A365D] text-sm font-semibold text-white transition-colors hover:bg-[#122845] disabled:opacity-40"
                >
                  {mode === "phone" ? (
                    <MessageCircle className="h-4 w-4" />
                  ) : mode === "sms" ? (
                    <Smartphone className="h-4 w-4" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                  {busy
                    ? "Sending…"
                    : mode === "phone"
                      ? "Send OTP on WhatsApp"
                      : mode === "sms"
                        ? "Send OTP by SMS"
                        : "Email me a code"}
                </button>
              </div>
            </>
          )}

          {sent && (
            <div className="mt-7 space-y-4">
              {devCode && (
                <div
                  data-testid="dev-otp-notice"
                  className="border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                >
                  WhatsApp delivery unavailable. Dev code:{" "}
                  <strong className="font-mono-num">{devCode}</strong>
                </div>
              )}
              <input
                data-testid="otp-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, requiredCodeLength))}
                placeholder={"_".repeat(requiredCodeLength)}
                className="h-14 w-full border border-slate-300 bg-white px-3 text-center font-mono-num text-2xl tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-[#1A365D]"
              />
              <button
                data-testid="verify-otp-btn"
                disabled={busy || code.length !== requiredCodeLength}
                onClick={verify}
                className="h-11 w-full bg-[#1A365D] text-sm font-semibold text-white transition-colors hover:bg-[#122845] disabled:opacity-40"
              >
                {busy ? "Verifying…" : "Verify & continue"}
              </button>
              <button
                data-testid="change-number-btn"
                onClick={() => {
                  setSent(false);
                  setCode("");
                  setDevCode(null);
                }}
                className="w-full text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-[#1A365D]"
              >
                {isPhoneMode ? "Change number" : "Change email"}
              </button>
            </div>
          )}

          <div className="my-8 flex items-center gap-3 text-[11px] uppercase tracking-widest text-slate-400">
            <span className="h-px flex-1 bg-slate-200" /> or <span className="h-px flex-1 bg-slate-200" />
          </div>

          <button
            data-testid="google-login-btn"
            onClick={googleLogin}
            className="h-11 w-full border border-slate-300 bg-white text-sm font-semibold text-slate-800 transition-colors hover:border-[#1A365D] hover:text-[#1A365D]"
          >
            Continue with Google
          </button>

          <Link
            to="/admin/login"
            data-testid="admin-login-link"
            className="mt-8 block text-center text-xs font-semibold uppercase tracking-widest text-slate-400 hover:text-[#1A365D]"
          >
            Admin sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
